import type { TelemetryPayload } from '@ukrop-nm/shared';
import valid from '../../../../samples/telemetry-valid.json';
import stale from '../../../../samples/telemetry-stale.json';
import wanFailure from '../../../../samples/telemetry-wan-failure.json';

export type SimScenario =
  | 'normal'
  | 'wan_failover'
  | 'critical_down'
  | 'sleeping_tablet'
  | 'passive_switch'
  | 'stale'
  | 'no_heartbeat'
  | 'reboot'
  | 'counter_reset'
  | 'duplicate_sequence';

export interface SimulatorState {
  scenario: SimScenario;
  payload: TelemetryPayload | null;
  lastHeartbeatAtMs: number | null;
  sequence: number;
  uptimeSeconds: number;
  events: string[];
}

function clone<T>(v: T): T {
  return structuredClone(v);
}

export function buildScenario(
  scenario: SimScenario,
  prev?: SimulatorState,
): SimulatorState {
  const now = Date.now();
  const baseSeq = prev?.sequence ?? 1500;
  const baseUptime = prev?.uptimeSeconds ?? 735210;
  const events = [...(prev?.events ?? [])];

  const withMeta = (payload: TelemetryPayload, seq: number, uptime: number): TelemetryPayload => ({
    ...payload,
    sequence: seq,
    uptimeSeconds: uptime,
    routerTime: new Date(now).toISOString(),
  });

  switch (scenario) {
    case 'normal': {
      const payload = withMeta(clone(valid) as TelemetryPayload, baseSeq + 1, baseUptime + 60);
      return {
        scenario,
        payload,
        lastHeartbeatAtMs: now,
        sequence: payload.sequence,
        uptimeSeconds: payload.uptimeSeconds,
        events: [...events, 'Heartbeat: normal'],
      };
    }
    case 'wan_failover': {
      const payload = withMeta(clone(wanFailure) as TelemetryPayload, baseSeq + 1, baseUptime + 60);
      events.push('WAN-ISP1 DOWN → active WAN-STAR');
      events.push('PHONE-03 OFFLINE');
      return {
        scenario,
        payload,
        lastHeartbeatAtMs: now,
        sequence: payload.sequence,
        uptimeSeconds: payload.uptimeSeconds,
        events,
      };
    }
    case 'critical_down': {
      const payload = withMeta(clone(valid) as TelemetryPayload, baseSeq + 1, baseUptime + 60);
      payload.targets = payload.targets.map((t) =>
        t.monitorKey === 'CORE-SWITCH-01'
          ? { ...t, status: 'offline', packetLossPercent: 100, changedAt: new Date(now).toISOString() }
          : t,
      );
      events.push('CORE-SWITCH-01 DOWN');
      return {
        scenario,
        payload,
        lastHeartbeatAtMs: now,
        sequence: payload.sequence,
        uptimeSeconds: payload.uptimeSeconds,
        events,
      };
    }
    case 'sleeping_tablet': {
      const payload = withMeta(clone(valid) as TelemetryPayload, baseSeq + 1, baseUptime + 60);
      payload.targets = payload.targets.map((t) =>
        t.monitorKey === 'TABLET-01'
          ? { ...t, status: 'offline', changedAt: new Date(now).toISOString() }
          : t,
      );
      events.push('TABLET-01 sleeping/inactive (not alarm)');
      return {
        scenario,
        payload,
        lastHeartbeatAtMs: now,
        sequence: payload.sequence,
        uptimeSeconds: payload.uptimeSeconds,
        events,
      };
    }
    case 'passive_switch': {
      return buildScenario('normal', prev);
    }
    case 'stale': {
      const payload = withMeta(clone(stale) as TelemetryPayload, baseSeq + 1, baseUptime + 60);
      return {
        scenario,
        payload,
        lastHeartbeatAtMs: now - 160_000,
        sequence: payload.sequence,
        uptimeSeconds: payload.uptimeSeconds,
        events: [...events, 'Stale telemetry window'],
      };
    }
    case 'no_heartbeat': {
      return {
        scenario,
        payload: prev?.payload ?? null,
        lastHeartbeatAtMs: now - 200_000,
        sequence: prev?.sequence ?? baseSeq,
        uptimeSeconds: prev?.uptimeSeconds ?? baseUptime,
        events: [...events, 'No heartbeat > 3 intervals → site_unreachable'],
      };
    }
    case 'reboot': {
      const payload = withMeta(clone(valid) as TelemetryPayload, 1, 15);
      events.push('RB2011 reboot detected (uptime drop, sequence reset)');
      return {
        scenario,
        payload,
        lastHeartbeatAtMs: now,
        sequence: payload.sequence,
        uptimeSeconds: payload.uptimeSeconds,
        events,
      };
    }
    case 'counter_reset': {
      const payload = withMeta(clone(valid) as TelemetryPayload, baseSeq + 1, baseUptime + 60);
      payload.interfaces = payload.interfaces.map((i) => ({
        ...i,
        rxBytes: 100,
        txBytes: 100,
      }));
      events.push('Interface counters reset');
      return {
        scenario,
        payload,
        lastHeartbeatAtMs: now,
        sequence: payload.sequence,
        uptimeSeconds: payload.uptimeSeconds,
        events,
      };
    }
    case 'duplicate_sequence': {
      const seq = prev?.sequence ?? baseSeq;
      const payload = withMeta(clone(valid) as TelemetryPayload, seq, baseUptime + 60);
      events.push(`Duplicate sequence ${seq} ignored`);
      return {
        scenario,
        payload,
        lastHeartbeatAtMs: prev?.lastHeartbeatAtMs ?? now,
        sequence: seq,
        uptimeSeconds: prev?.uptimeSeconds ?? payload.uptimeSeconds,
        events,
      };
    }
    default:
      return buildScenario('normal', prev);
  }
}
